/*
 * ISW-30 — stop off-screen carousels from animating.
 *
 * Every Flickity carousel the theme builds with autoplay starts its player as soon as
 * global.min.js initialises it, and keeps it running for the life of the page. Each transition
 * runs Flickity's physics loop (`animate()` -> `positionSlider()` -> `setTranslateX()`) once per
 * animation frame, and the loop re-arms every `au_time` seconds forever. Profiling
 * www.iccshopping.com/ put that loop at the top of the page's CPU profile: 2,057 ms across 1,080
 * calls, in bursts every ~3 s from +5.3 s to +45 s, plus the compositing each transform triggers.
 * The homepage runs three autoplaying carousels — slideshow (5 s), logo_list (3 s) and
 * featured-collection (4 s) — and two of them sit below the fold, so that work was being spent
 * animating content nobody could see, for as long as the tab stayed open.
 *
 * This pauses a carousel's player only while the carousel is outside the viewport, and lets it
 * run normally whenever it is on screen. Carousels are still constructed at the usual point in
 * the ready handler, so drag, arrows, dots and layout are untouched.
 *
 * An earlier version also held autoplay back until the page had finished loading. Measured over
 * six Lighthouse runs per arm that cost more than it gained: desktop TTI slid 7.5 s -> 8.6 s, and
 * on mobile the hero not advancing during load pushed Speed Index 12.0 s -> 16.3 s and the
 * performance score down a point, because a static first slide fills the viewport more slowly
 * than a rotating one. Only the off-screen half of the idea survives, and it is the half with no
 * trade-off: it changes nothing a visitor is looking at.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window) || typeof jQuery_T4NT === 'undefined') return;

  var GUARDED = '_isw30AutoplayGuarded';
  var guarded = [];

  /* The theme stores the Flickity instance under jQuery data, not on the element, and does so
     synchronously inside T4SThemeSP.Carousel — so it is already there by the time the
     `flickityt4s-enabled` class we select on has been added. Carousels with autoplay switched
     off have no player worth touching. */
  function playerOf(el) {
    var flkty = jQuery_T4NT(el).data('flickityt4s');
    return flkty && flkty.player && flkty.options && flkty.options.autoPlay ? flkty : null;
  }

  function guard(el) {
    if (el[GUARDED]) return;
    if (!playerOf(el)) return;

    el[GUARDED] = true;
    var entry = { el: el, onScreen: true };
    guarded.push(entry);

    /* The observer's first callback arrives before any autoplay interval can elapse (the
       shortest on this theme is 3 s), so an off-screen carousel is paused before it animates,
       and an on-screen one is never touched at all. */
    new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          entry.onScreen = entries[i].isIntersecting;
          var flkty = playerOf(el);
          if (!flkty) continue;
          entry.onScreen ? flkty.unpausePlayer() : flkty.pausePlayer();
        }
      },
      /* A margin so a carousel is already moving by the time it is scrolled to, rather than
         visibly starting late. */
      { rootMargin: '200px 0px' }
    ).observe(el);
  }

  function guardAll() {
    var els = document.querySelectorAll('.flickityt4s-enabled');
    for (var i = 0; i < els.length; i++) guard(els[i]);
  }

  /* Flickity's player has its own `visibilitychange` listener that unpauses on the way back to a
     visible tab, which would override our deliberate off-screen pause. Re-assert after it runs. */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    setTimeout(function () {
      for (var i = 0; i < guarded.length; i++) {
        if (guarded[i].onScreen) continue;
        var flkty = playerOf(guarded[i].el);
        if (flkty) flkty.pausePlayer();
      }
    }, 0);
  });

  /* This file is deferred directly after global.min.js, so T4SThemeSP already exists but its
     ready handler — the one calling initCarousel() — has not run yet. Registering ours second
     means jQuery calls it immediately afterwards. */
  jQuery_T4NT(document).ready(guardAll);

  /* Carousels built after the initial sweep: product galleries and tab panels created by
     theme.min.js, quick view, load-more, and section re-renders in the theme editor. A carousel
     the sweeps miss simply keeps the theme's original behaviour — it autoplays wherever it is —
     so missing one can forgo the saving but can never break a slider. */
  window.addEventListener('load', guardAll);
  document.addEventListener('shopify:section:load', guardAll);

  /* Read-only view of the guard's state, for QA and for diagnosing a "slider stopped" report. */
  window.__isw30Guard = {
    count: function () { return guarded.length; },
    states: function () {
      return guarded.map(function (g) {
        var flkty = playerOf(g.el);
        return {
          section: (g.el.closest('.shopify-section') || {}).id || null,
          onScreen: g.onScreen,
          state: flkty && flkty.player ? flkty.player.state : null,
        };
      });
    },
  };
})();
